import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useFormContext } from 'react-hook-form';
import MinusIcon from '@/app/components/questionnaireV2/icons/minus-icon';
import PlusIcon from '@/app/components/questionnaireV2/icons/plus-icon';

interface NumberStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  questionSlug?: string;
}

export const NumberStepper = ({ value, onChange, min = 0, max, questionSlug }: NumberStepperProps) => {
  const [isMinusHovered, setIsMinusHovered] = useState(false);
  const [isPlusHovered, setIsPlusHovered] = useState(false);
  const [isMinusActive, setIsMinusActive] = useState(false);
  const [isPlusActive, setIsPlusActive] = useState(false);
  const safeValue = value ?? min;
  const [displayValue, setDisplayValue] = useState<string>(safeValue.toString());
  const [displayError, setDisplayError] = useState<string | null>(null);

  useEffect(() => {
    const currentValue = value ?? min;
    if (displayValue !== currentValue.toString()) {
      setDisplayValue(currentValue.toString());
    }
  }, [value, min, displayValue]);

  const isMinDisabled = safeValue <= min;
  const isMaxDisabled = max != null && safeValue >= max;

  useEffect(() => {
    if (displayError) {
      const timer = setTimeout(() => {
        setDisplayError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [displayError]);

  const handleDecrement = () => {
    if (isMinDisabled) {
      setDisplayError(`Minimum value is ${min}`);
      return;
    }
    const newValue = safeValue - 1;
    if (newValue >= min) {
      onChange(newValue);
      setDisplayError(null);
    }
  };

  const handleIncrement = () => {
    if (isMaxDisabled) {
      setDisplayError(`Maximum value is ${max}`);
      return;
    }
    const newValue = safeValue + 1;
    if (!max || newValue <= max) {
      onChange(newValue);
      setDisplayError(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);

    // Only update parent value if it's a valid number
    const newValue = parseInt(inputValue);
    if (!isNaN(newValue)) {
      onChange(newValue);
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue === '') {
      // If empty, set to minimum value
      onChange(min);
      setDisplayValue(min.toString());
      return;
    }

    const newValue = parseInt(inputValue) || min;
    if (newValue < min) {
      onChange(min);
      setDisplayValue(min.toString());
    } else if (max && newValue > max) {
      onChange(max);
      setDisplayValue(max.toString());
    } else {
      onChange(newValue);
      setDisplayValue(newValue.toString());
    }
  };

  const getButtonClassName = (isHovered: boolean, isActive: boolean, isDisabled: boolean) => {
    if (isDisabled) {
      return 'number-stepper-btn-disabled';
    }
    if (isActive) {
      return 'number-stepper-btn-active';
    }
    return isHovered ? 'number-stepper-btn-hover' : 'number-stepper-btn-default';
  };

  return (
    <div className="questionnaire-question-content">
      {/* Stepper Container */}
      <div className="number-stepper-container">
        {/* Background with border and shadow */}
        <div className="number-stepper-bg" />

        <motion.button
          key="minus"
          type="button"
          initial={{ opacity: 1, x: 0 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          onClick={handleDecrement}
          onMouseEnter={() => setIsMinusHovered(true)}
          onMouseLeave={() => setIsMinusHovered(false)}
          onTouchStart={() => setIsMinusActive(true)}
          onTouchEnd={() => setIsMinusActive(false)}
          onMouseDown={() => setIsMinusActive(true)}
          onMouseUp={() => setIsMinusActive(false)}
          className={`absolute top-[15px] left-[15px] z-1 flex h-[50px] w-[50px] items-center justify-center rounded-full transition-all duration-200 ${
            isMinDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:scale-105 active:scale-95'
          } ${getButtonClassName(isMinusHovered, isMinusActive, isMinDisabled)}`}
          aria-label="Decrease value"
        >
          <MinusIcon />
        </motion.button>
        <motion.button
          key="plus"
          type="button"
          initial={{ opacity: 1, x: 0 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          onClick={handleIncrement}
          onMouseEnter={() => setIsPlusHovered(true)}
          onMouseLeave={() => setIsPlusHovered(false)}
          onTouchStart={() => setIsPlusActive(true)}
          onTouchEnd={() => setIsPlusActive(false)}
          onMouseDown={() => setIsPlusActive(true)}
          onMouseUp={() => setIsPlusActive(false)}
          className={`absolute top-[15px] right-[15px] z-1 flex h-[50px] w-[50px] items-center justify-center rounded-full transition-all duration-200 ${
            isMaxDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:scale-105 active:scale-95'
          } ${getButtonClassName(isPlusHovered, isPlusActive, isMaxDisabled)}`}
          aria-label="Increase value"
        >
          <PlusIcon />
        </motion.button>
        <div className="absolute inset-1 flex items-center justify-center">
          <input
            type="number"
            inputMode="numeric"
            value={displayValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            className="input-field-number-stepper"
          />
        </div>
      </div>

      <AnimatePresence>
        {displayError && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2 }}
            className="stepper-limit-message"
          >
            {displayError}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
